import { RowCrudStatus } from "../../common/types";
import {
  TenantDocument,
  TenantDocumentCreateMutationVariables,
  TenantDocumentUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { blankToNull } from "../../../common/blankToNull";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import {
  getFileVariableValue,
  getNotNullFileVariableValue,
} from "../../common/utils";
import { DocumentProcessingStatus } from "../../../api/graphql";
import { ChunkingStrategy } from "../../../api/graphql";

let uniqueCounter = 1;
export type TenantDocumentValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name: string;
  description: string;
  file: string;
  file_data: FileCRUDType;
  __file_files: FileCRUDType[];
  fileType: string;
  fileSize: number;
  status: DocumentProcessingStatus;
  chunkCount: number | undefined;
  uploadedAt: Date | null;
  processedAt: Date | null;
  processingError: string;
  contentType: string;
  chunkingStrategy: ChunkingStrategy;
  chunkingSeparatorRegex: string;
  chunkingMaxTokens: number | undefined;
  chunkingOverlapTokens: number | undefined;
};

export type TenantDocumentErrorsType = {
  name?: { message: string; fieldLabel: string };
  description?: { message: string; fieldLabel: string };
  file?: { message: string; fieldLabel: string };
  fileType?: { message: string; fieldLabel: string };
  fileSize?: { message: string; fieldLabel: string };
  status?: { message: string; fieldLabel: string };
  chunkCount?: { message: string; fieldLabel: string };
  uploadedAt?: { message: string; fieldLabel: string };
  processedAt?: { message: string; fieldLabel: string };
  processingError?: { message: string; fieldLabel: string };
  contentType?: { message: string; fieldLabel: string };
  chunkingStrategy?: { message: string; fieldLabel: string };
  chunkingSeparatorRegex?: { message: string; fieldLabel: string };
  chunkingMaxTokens?: { message: string; fieldLabel: string };
  chunkingOverlapTokens?: { message: string; fieldLabel: string };
};

export function TenantDocumentValuesInit(): TenantDocumentValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name: "",
    description: "",
    file: "",
    file_data: fileCRUDInit(),
    __file_files: [],
    fileType: "",
    fileSize: 0,
    status: "" as DocumentProcessingStatus,
    chunkCount: undefined,
    uploadedAt: null,
    processedAt: null,
    processingError: "",
    contentType: "",
    chunkingStrategy: "auto" as ChunkingStrategy,
    chunkingSeparatorRegex: "",
    chunkingMaxTokens: 500,
    chunkingOverlapTokens: 50,
  };
}

export type TenantDocumentType = TenantDocument | TenantDocumentValuesType;

export function tenantDocumentDisplayId(
  source: Partial<TenantDocument>,
  _langStore: LangStore
) {
  return source.name ?? _langStore.emptyStringSymbol;
}

export function getTenantDocumentValues(
  source: TenantDocumentType,
  _langStore: LangStore,
  _duplicated: boolean = false
): TenantDocumentValuesType {
  const entityValues = TenantDocumentValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : tenantDocumentDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name = source.name;
  entityValues.description = source.description || "";
  entityValues.__file_files =
    source.file_data && !_duplicated
      ? [
          {
            id:
              "id" in source.file_data && source.file_data.id
                ? source.file_data.id
                : `generated_${uniqueCounter++}`,
            name: source.file_data.name,
            description: source.file_data.description || undefined,
            size: source.file_data.size || 0,
            formattedSize: source.file_data.size
              ? formatBytes(source.file_data.size)
              : "",
            mimetype: source.file_data.mimetype,
            url: source.file_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.file = source.file && !_duplicated ? source.file : "";
  entityValues.file_data =
    source.file_data && "__status__" in source.file_data
      ? source.file_data
      : source.file_data
      ? {
          id: ("id" in source.file_data && source.file_data.id
            ? source.file_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.file_data.name,
          description: source.file_data.description || undefined,
          size: source.file_data.size || 0,
          formattedSize: source.file_data.size
            ? formatBytes(source.file_data.size)
            : "",
          mimetype: source.file_data.mimetype,
          url: source.file_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.fileType = source.fileType;
  entityValues.fileSize = source.fileSize || 0;
  entityValues.status = source.status;
  entityValues.chunkCount =
    (source.chunkCount as unknown) === "" ||
    source.chunkCount === undefined ||
    source.chunkCount === null
      ? undefined
      : source.chunkCount;
  entityValues.uploadedAt = new Date(source.uploadedAt);
  entityValues.processedAt = source.processedAt
    ? new Date(source.processedAt)
    : null;
  entityValues.processingError = source.processingError || "";
  entityValues.contentType = source.contentType || "";
  entityValues.chunkingStrategy = source.chunkingStrategy;
  entityValues.chunkingSeparatorRegex = source.chunkingSeparatorRegex || "";
  entityValues.chunkingMaxTokens =
    (source.chunkingMaxTokens as unknown) === "" ||
    source.chunkingMaxTokens === undefined ||
    source.chunkingMaxTokens === null
      ? undefined
      : source.chunkingMaxTokens;
  entityValues.chunkingOverlapTokens =
    (source.chunkingOverlapTokens as unknown) === "" ||
    source.chunkingOverlapTokens === undefined ||
    source.chunkingOverlapTokens === null
      ? undefined
      : source.chunkingOverlapTokens;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getTenantDocumentUpdateVariables(
  values: TenantDocumentValuesType
): TenantDocumentUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    name: values.name,
    description: blankToNull(values.description),
    file: getFileVariableValue(values.__file_files),
    processingError: blankToNull(values.processingError),
    contentType: blankToNull(values.contentType),
    chunkingStrategy: values.chunkingStrategy,
    chunkingSeparatorRegex: blankToNull(values.chunkingSeparatorRegex),
    chunkingMaxTokens:
      values.chunkingMaxTokens === undefined ? null : values.chunkingMaxTokens,
    chunkingOverlapTokens:
      values.chunkingOverlapTokens === undefined
        ? null
        : values.chunkingOverlapTokens,
  };
}

export function getTenantDocumentCreateVariables(
  values: TenantDocumentValuesType
): TenantDocumentCreateMutationVariables {
  return {
    name: values.name,
    description: blankToNull(values.description),
    file: getNotNullFileVariableValue(values.__file_files),
    processingError: blankToNull(values.processingError),
    contentType: blankToNull(values.contentType),
    chunkingStrategy: values.chunkingStrategy,
    chunkingSeparatorRegex: blankToNull(values.chunkingSeparatorRegex),
    chunkingMaxTokens:
      values.chunkingMaxTokens === undefined ? null : values.chunkingMaxTokens,
    chunkingOverlapTokens:
      values.chunkingOverlapTokens === undefined
        ? null
        : values.chunkingOverlapTokens,
  };
}
